import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';
import {
  Check,
  ShieldCheck,
  Globe,
  FileText,
  CheckSquare,
  Clock,
  ArrowRight,
  Copy,
} from 'lucide-react';
import ShieldLogo from '../components/ShieldLogo';
import CircularProgress from '../components/CircularProgress';
import Footer from '../components/Footer';
import { useCountUpOnVisible } from '../lib/useCountUpOnVisible';

const COMPANY = {
  name: 'Acme Inc.',
  profile:
    'Acme Inc. is a B2B SaaS company with a 5-person team serving US and EU customers. Built on AWS with Stripe billing, the platform handles customer account data and payment metadata under SOC2 and GDPR frameworks.',
};

const ROADMAP = [
  {
    title: 'Implement SOC 2 Type I Assessment',
    desc:
      'Engage a qualified auditor to begin SOC 2 Type I evaluation focused on Security and Availability criteria. Start collecting evidence across your AWS infrastructure controls.',
    level: 'High',
  },
  {
    title: 'Establish GDPR Compliance Framework',
    desc:
      'With EU customers in scope, formalise data processing agreements, privacy notices, data subject rights procedures, and a 72-hour breach notification workflow.',
    level: 'High',
  },
  {
    title: 'Roll out SSO + Least-privilege Access',
    desc:
      'Move every production system behind SSO. Tag roles by least-privilege and run a quarterly access review across engineering and ops.',
    level: 'Medium',
  },
  {
    title: 'Formalise Vendor Risk Reviews',
    desc:
      'Catalogue your sub-processors (AWS, Stripe, Resend, …), maintain DPA records, and re-review every 12 months or whenever a vendor materially changes.',
    level: 'Medium',
  },
  {
    title: 'Quarterly Security Awareness Training',
    desc:
      'Run a 30-minute phishing and data-handling refresher every quarter. Log attendance and quiz outcomes for the audit trail.',
    level: 'Low',
  },
];

const TASKS = [
  { label: 'Enable MFA across all production accounts', done: true },
  { label: 'Publish the updated incident response runbook', done: false },
  { label: 'Run quarterly access review (engineering)', done: false },
  { label: 'Confirm DPA on file for every sub-processor', done: false },
];

const SCORE = 62;
const SCORE_CHECKLIST = [
  { label: 'Encryption at rest and in transit', done: true },
  { label: 'Documented incident response plan', done: false },
  { label: 'Vendor risk assessment', done: false },
];

const TRUST_POLICIES = [
  { label: 'Data Handling Policy', status: 'in_place' },
  { label: 'Access Control Policy', status: 'in_place' },
  { label: 'Incident Response Policy', status: 'in_progress' },
];

const HASH_TO_TAB = {
  '#trust-page': 'trust',
  '#policies': 'policy',
  '#roadmap': 'roadmap',
  '#score': 'score',
  '#tasks': 'tasks',
};

const DemoPage = () => {
  const location = useLocation();
  const initialTab = HASH_TO_TAB[location.hash] || 'roadmap';
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Banner */}
      <div data-testid="demo-banner" className="bg-[#0a5c44] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm text-center">
          <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
          <span>This is a sample report, see what Verilock generates for your business.</span>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
          >
            <ShieldLogo size={24} />
            <span
              className="text-xl sm:text-2xl font-semibold"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Verilock
            </span>
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#0a5c44] font-medium border border-[#0a5c44]/30 bg-[#0a5c44]/5 px-2.5 py-1 rounded-full">
            Sample report · {COMPANY.name}
          </span>
        </div>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
        {/* Title strip */}
        <div className="mb-6 sm:mb-8">
          <p className="text-[11px] font-semibold tracking-wider uppercase text-[#0a5c44] mb-2">
            Demo · {COMPANY.name}
          </p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-normal leading-tight mb-2"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            A look inside a Verilock report
          </h1>
          <p className="text-sm sm:text-base text-[#1a1a1a]/70 max-w-2xl">
            Below is exactly what a 5-person B2B SaaS team would see after completing the wizard. All content is illustrative.
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={initialTab} className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList
              data-testid="demo-tabs-list"
              className="inline-flex sm:flex w-max sm:w-full gap-1 bg-[#f5f3ef] p-1 border border-[#e0ddd8] rounded-xl"
            >
              <DemoTab value="roadmap" icon={<FileText className="w-4 h-4" />}>Roadmap</DemoTab>
              <DemoTab value="policy" icon={<FileText className="w-4 h-4" />}>Data Handling Policy</DemoTab>
              <DemoTab value="score" icon={<ShieldCheck className="w-4 h-4" />}>Compliance Score</DemoTab>
              <DemoTab value="trust" icon={<Globe className="w-4 h-4" />}>Trust Page</DemoTab>
              <DemoTab value="tasks" icon={<CheckSquare className="w-4 h-4" />}>This Month</DemoTab>
            </TabsList>
          </div>

          <div className="mt-6 sm:mt-8">
            <TabsContent value="roadmap"><RoadmapPanel /></TabsContent>
            <TabsContent value="policy"><PolicyPanel /></TabsContent>
            <TabsContent value="score"><ScorePanel /></TabsContent>
            <TabsContent value="trust"><TrustPanel /></TabsContent>
            <TabsContent value="tasks"><TasksPanel /></TabsContent>
          </div>

          {/* Sticky-feel CTA at the bottom of every tab */}
          <div className="mt-8 sm:mt-10">
            <CtaButton />
          </div>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

/* -------------------- Building blocks -------------------- */

const DemoTab = ({ value, icon, children }) => (
  <TabsTrigger
    value={value}
    data-testid={`demo-tab-${value}`}
    className="
      inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-sm rounded-lg
      whitespace-nowrap transition-all
      text-[#1a1a1a]/55 font-normal border border-transparent
      hover:text-[#1a1a1a]/80
      data-[state=active]:bg-white
      data-[state=active]:text-[#0a5c44]
      data-[state=active]:font-bold
      data-[state=active]:border-[#0a5c44]
      data-[state=active]:shadow-[0_2px_6px_rgba(0,0,0,0.15)]
    "
  >
    {icon}
    {children}
  </TabsTrigger>
);

const PriorityBadge = ({ level }) => {
  const styles = {
    High: 'bg-red-50 text-red-700 border-red-200',
    Medium: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span
      className={`inline-flex items-center text-[10px] font-semibold tracking-wide uppercase border px-2 py-0.5 rounded-full shrink-0 ${styles[level]}`}
    >
      {level}
    </span>
  );
};

const SectionCard = ({ title, subtitle, children, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#e0ddd8] rounded-2xl p-5 sm:p-7"
  >
    <div className="mb-4 sm:mb-5">
      <h3
        className="text-xl sm:text-2xl font-normal leading-tight"
        style={{ fontFamily: 'Instrument Serif, serif' }}
      >
        {title}
      </h3>
      {subtitle && (
        <p className="text-sm text-[#1a1a1a]/65 mt-1.5">{subtitle}</p>
      )}
    </div>
    {children}
  </div>
);

/* -------------------- Tab panels -------------------- */

function RoadmapPanel() {
  return (
    <SectionCard
      testid="demo-roadmap-panel"
      title="Compliance roadmap"
      subtitle={`A prioritised action plan tailored to ${COMPANY.name}.`}
    >
      <ul className="space-y-3">
        {ROADMAP.map((it) => (
          <li
            key={it.title}
            className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-xl p-4 sm:p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base font-semibold text-[#1a1a1a] mb-1.5">
                  {it.title}
                </p>
                <p className="text-xs sm:text-sm text-[#1a1a1a]/70 leading-relaxed">
                  {it.desc}
                </p>
              </div>
              <PriorityBadge level={it.level} />
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function PolicyPanel() {
  return (
    <SectionCard
      testid="demo-policy-panel"
      title="Data Handling Policy"
      subtitle={`Owner: ${COMPANY.name} · Effective: this quarter · Reviewed: quarterly`}
    >
      <div className="flex items-center justify-end -mt-2 mb-4">
        <span className="inline-flex items-center gap-1.5 text-xs text-[#1a1a1a]/55 border border-[#e0ddd8] px-2.5 py-1 rounded-lg">
          <Copy className="w-3.5 h-3.5" /> Copy to clipboard
        </span>
      </div>
      <div className="space-y-5">
        <PolicySection
          label="Policy statement"
          body={`${COMPANY.name} treats customer data as a shared trust obligation. This policy defines how data is collected, processed, stored, and protected across every service we operate, and applies to every employee, contractor, and integrated vendor.`}
        />
        <PolicySection
          label="Scope"
          body="All systems that store, process, or transmit customer data — including AWS production accounts, the Stripe billing integration, and internal analytics — are in scope. Personal devices used to access production systems are also in scope."
        />
        <PolicySection
          label="Data collection"
          body="Only the minimum data required to deliver the product is collected. Collection points are documented in the data inventory and reviewed each quarter by the security lead. Customer PII is never collected for marketing without explicit opt-in."
        />
        <PolicySection
          label="Data retention"
          body="Customer records are retained for the active subscription period plus 30 days. Encrypted backups follow a 90-day rotation. Customers may request deletion at any time and receive confirmation within 14 days."
        />
        <PolicySection
          label="Roles & responsibilities"
          body="The CTO owns this policy. The on-call engineer is responsible for first-line incident response. All employees complete quarterly security training and acknowledge this policy at hire and annually."
        />
      </div>
    </SectionCard>
  );
}

const PolicySection = ({ label, body }) => (
  <div>
    <p className="text-[11px] font-semibold tracking-wider uppercase text-[#1a1a1a]/55 mb-1.5">
      {label}
    </p>
    <p className="text-sm text-[#1a1a1a]/80 leading-relaxed">{body}</p>
  </div>
);

function ScorePanel() {
  const { ref, display, complete } = useCountUpOnVisible(SCORE, 1500);
  const shown = complete ? SCORE : display;
  return (
    <SectionCard
      testid="demo-score-panel"
      title="Compliance score"
      subtitle="A real-time snapshot of your security posture. Complete the checklist to improve it."
    >
      <div ref={ref} className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 mb-6">
        <CircularProgress value={shown} size={140} strokeWidth={10} />
        <div className="text-center sm:text-left">
          <p className="text-[11px] font-semibold tracking-wider uppercase text-[#0a5c44] mb-1.5">
            Your score
          </p>
          <h4
            className="text-2xl sm:text-3xl font-normal leading-tight mb-1.5"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Solid foundation, room to grow
          </h4>
          <p className="text-sm text-[#1a1a1a]/65 max-w-md">
            {SCORE}/100. {COMPANY.name} has the basics covered. Closing the next 2 items below will lift you above the typical Series A baseline.
          </p>
        </div>
      </div>
      <ul className="space-y-2 max-w-md">
        {SCORE_CHECKLIST.map((t) => (
          <li
            key={t.label}
            className="flex items-center gap-3 bg-[#f5f3ef] border border-[#e0ddd8] rounded-lg px-3.5 py-2.5"
          >
            <span
              className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                t.done
                  ? 'bg-[#0a5c44] border-[#0a5c44]'
                  : 'bg-white border-[#1a1a1a]/25'
              }`}
            >
              {t.done && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </span>
            <span
              className={`text-sm ${
                t.done ? 'text-[#1a1a1a]/45 line-through' : 'text-[#1a1a1a]/85'
              }`}
            >
              {t.label}
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function TrustPanel() {
  return (
    <SectionCard
      testid="demo-trust-panel"
      title="Trust Page preview"
      subtitle={`What prospects see when ${COMPANY.name} shares its public security page.`}
    >
      <div className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-xl p-5 sm:p-6 mb-5">
        <p className="text-[11px] font-semibold tracking-wider uppercase text-[#0a5c44] mb-2">
          Our commitment
        </p>
        <p className="text-sm text-[#1a1a1a]/80 leading-relaxed">
          {COMPANY.profile} We protect customer data with encryption in transit and at rest, least-privilege access, and a documented incident response plan reviewed every quarter.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {TRUST_POLICIES.map((p) => {
          const inPlace = p.status === 'in_place';
          return (
            <div
              key={p.label}
              className="bg-white border border-[#e0ddd8] rounded-xl p-4 sm:p-5"
            >
              {inPlace ? (
                <Check className="w-5 h-5 text-[#0a5c44] mb-2.5" />
              ) : (
                <Clock className="w-5 h-5 text-yellow-600 mb-2.5" />
              )}
              <p className="text-sm font-semibold text-[#1a1a1a] leading-snug">
                {p.label}
              </p>
              <p
                className={`text-[11px] mt-1.5 ${
                  inPlace ? 'text-[#0a5c44]' : 'text-yellow-700'
                }`}
              >
                {inPlace ? 'In place & reviewed' : 'In progress'}
              </p>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

function TasksPanel() {
  const done = TASKS.filter((t) => t.done).length;
  return (
    <SectionCard
      testid="demo-tasks-panel"
      title="This month's tasks"
      subtitle={`${done} of ${TASKS.length} complete · resets the first of next month`}
    >
      <ul className="space-y-2">
        {TASKS.map((t) => (
          <li
            key={t.label}
            className="flex items-center gap-3 bg-[#f5f3ef] border border-[#e0ddd8] rounded-lg px-3.5 py-3"
          >
            <span
              className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                t.done
                  ? 'bg-[#0a5c44] border-[#0a5c44]'
                  : 'bg-white border-[#1a1a1a]/25'
              }`}
            >
              {t.done && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </span>
            <span
              className={`text-sm sm:text-base ${
                t.done ? 'text-[#1a1a1a]/45 line-through' : 'text-[#1a1a1a]/85'
              }`}
            >
              {t.label}
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

/* -------------------- Bottom CTA -------------------- */

function CtaButton() {
  return (
    <Link
      to="/wizard"
      data-testid="demo-cta-button"
      className="inline-flex w-full items-center justify-center gap-2 bg-[#0a5c44] text-white hover:bg-[#084d39] transition-colors h-14 rounded-xl text-base font-semibold px-6"
    >
      Get your free compliance report
      <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

export default DemoPage;
